import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/api";

const AppContext = createContext(null);

const CURRENCY_META = {
  USD: { symbol: "$", position: "before", decimals: 2, locale: "en-US" },
  EUR: { symbol: "€", position: "after", decimals: 2, locale: "fr-FR" },
  GBP: { symbol: "£", position: "before", decimals: 2, locale: "en-GB" },
  JPY: { symbol: "¥", position: "before", decimals: 0, locale: "ja-JP" },
  CHF: { symbol: "CHF", position: "after", decimals: 2, locale: "de-CH" },
  CAD: { symbol: "C$", position: "before", decimals: 2, locale: "en-CA" },
  AUD: { symbol: "A$", position: "before", decimals: 2, locale: "en-AU" },
};

// Static fallback FX rates (used until live rates load from backend)
const STATIC_FX = {
  USD: 1.0, EUR: 0.92, GBP: 0.79, JPY: 155.0,
  CHF: 0.89, CAD: 1.36, AUD: 1.52,
};

const LANG_TO_LOCALE = {
  fr: "fr-FR", en: "en-US", es: "es-ES", de: "de-DE",
  it: "it-IT", pt: "pt-PT", ar: "ar-SA", zh: "zh-CN",
};

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("algo_lang") || "fr");
  const [currency, setCurrency] = useState(() => localStorage.getItem("algo_currency") || "USD");
  const [accountCurrency, setAccountCurrency] = useState(() => localStorage.getItem("algo_account_currency") || "USD");
  const [timezone, setTimezone] = useState(() => localStorage.getItem("algo_tz") || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  const [translations, setTranslations] = useState({ ui: {}, lexicon_easy: {} });
  const [fxRates, setFxRates] = useState(STATIC_FX);
  const [fxSource, setFxSource] = useState("static");
  const [loading, setLoading] = useState(true);

  const isRTL = lang === "ar";

  // Load translations
  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/i18n/translations`, { params: { lang } })
      .then((r) => { if (alive) setTranslations({ ui: r.data.ui, lexicon_easy: r.data.lexicon_easy }); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [lang]);

  // Load live FX rates every 15 min
  useEffect(() => {
    let alive = true;
    const loadFx = () =>
      axios.get(`${API}/fx/rates`)
        .then((r) => {
          if (!alive) return;
          setFxRates({ ...STATIC_FX, ...(r.data.rates || {}) });
          setFxSource(r.data.source || "unknown");
        })
        .catch(() => {});
    loadFx();
    const id = setInterval(loadFx, 15 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    localStorage.setItem("algo_lang", lang);
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
  }, [lang, isRTL]);

  useEffect(() => { localStorage.setItem("algo_currency", currency); }, [currency]);
  useEffect(() => { localStorage.setItem("algo_account_currency", accountCurrency); }, [accountCurrency]);
  useEffect(() => { localStorage.setItem("algo_tz", timezone); }, [timezone]);

  const t = useCallback((key, fallback) =>
    translations.ui[key] || fallback || key, [translations]);

  const lexEasy = useCallback((code) =>
    translations.lexicon_easy?.[code]?.easy || "", [translations]);

  // Currency formatting with live rates + Intl locale
  const fmtCurrency = useCallback((amountUsd, opts = {}) => {
    const meta = CURRENCY_META[currency] || CURRENCY_META.USD;
    const rate = fxRates[currency] || STATIC_FX[currency] || 1.0;
    const converted = (amountUsd || 0) * rate;
    const decimals = opts.decimals ?? meta.decimals;
    const num = new Intl.NumberFormat(meta.locale, {
      minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    }).format(converted);
    return meta.position === "before" ? `${meta.symbol}${num}` : `${num} ${meta.symbol}`;
  }, [currency, fxRates]);

  // Convert an arbitrary amount from any currency to another (uses live rates)
  const convertAmount = useCallback((amount, from, to) => {
    if (from === to) return amount;
    const fromR = fxRates[from] || 1.0;
    const toR = fxRates[to] || 1.0;
    const usd = amount / fromR;
    return usd * toR;
  }, [fxRates]);

  // Locale-aware date/number formatting
  const fmtDate = useCallback((dateOrIso, opts = {}) => {
    const d = typeof dateOrIso === "string" ? new Date(dateOrIso) : dateOrIso;
    return new Intl.DateTimeFormat(LANG_TO_LOCALE[lang] || "en-US", {
      timeZone: timezone,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
      ...opts,
    }).format(d);
  }, [lang, timezone]);

  const fmtNumber = useCallback((n, opts = {}) => {
    return new Intl.NumberFormat(LANG_TO_LOCALE[lang] || "en-US", opts).format(n);
  }, [lang]);

  const value = {
    lang, setLang, currency, setCurrency,
    accountCurrency, setAccountCurrency, timezone, setTimezone,
    isRTL, t, lexEasy, fmtCurrency, convertAmount, fmtDate, fmtNumber,
    fxRates, fxSource, loading, translations,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
}

export const LANGS = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
];

export const CURRENCIES_LIST = [
  { code: "USD", symbol: "$", label: "US Dollar" },
  { code: "EUR", symbol: "€", label: "Euro" },
  { code: "GBP", symbol: "£", label: "British Pound" },
  { code: "JPY", symbol: "¥", label: "Japanese Yen" },
  { code: "CHF", symbol: "CHF", label: "Swiss Franc" },
  { code: "CAD", symbol: "C$", label: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", label: "Australian Dollar" },
];
